import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { rescueFosterHomesQuery, type RescueFosterHomesData } from '../lib/rescueQueries';
import { adaptFosterHomes, type FosterHome } from '../lib/fosterAdapters';

interface UseFosterHomesResult {
  homes: FosterHome[];
  loading: boolean;
  /**
   * Whether `homes` is an answer rather than a default. Distinct from `!loading`, which is also false
   * for a skipped query — so callers that must not mistake "no roster fetched" for "roster is empty"
   * (anything deciding whether a foster is already saved) have to read this instead.
   */
  loaded: boolean;
  error: boolean;
  refetch: () => void;
}

/** The rescue's saved foster homes. Unpaginated on the backend, so the table paginates locally. */
export default function useFosterHomes(options?: { skip?: boolean }): UseFosterHomesResult {
  const { data, loading, error, refetch } = useQuery<RescueFosterHomesData>(rescueFosterHomesQuery, {
    skip: options?.skip,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const homes = useMemo(() => adaptFosterHomes(data?.rescueFosterHomes ?? []), [data]);

  return {
    homes,
    loading,
    loaded: data !== undefined,
    error: !!error,
    // Wrapped: callers wire this straight to onClick, and Apollo merges any truthy
    // argument into `variables` — a React event would land in the request payload.
    refetch: () => {
      void refetch();
    },
  };
}
